/**
 * /zh-tw/meeting-minutes-ai — 會議記錄 reframe bridge (Traditional Chinese).
 *
 * Targets 會議記錄 ai (2.1k) / 會議記錄 (800) / ai 會議記錄 (450). Gives a real
 * 會議記錄 format, then pivots to 決策紀錄 (decision log).
 */
import { MarketingShell } from './MarketingShell';

export function ZhTwMeetingMinutes() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/meeting-minutes-ai"
      hreflangLocales={['zhtw']}
      documentTitle="AI 會議記錄怎麼做｜範本＋留住「決策」的方法 · Draftlize"
      documentDescription="用 AI 自動產生會議記錄，附上可直接套用的範本。更重要的是教你把會議真正的價值——決策——完整留下來。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 會議記錄</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            AI 會議記錄，<br />
            <em>與會後真正該留下的東西。</em>
          </h1>
          <p className="lede">
            先給你一份能直接套用的會議記錄範本，也會講怎麼用 AI 自動產生。但重點在後面：就算會議記錄做得再漂亮，會議真正的價值——你們下的<em>決策</em>和理由——還是會躺在資料夾裡，幾週後就過時。會議記錄是「記錄」，不是活的產品脈絡。這頁要談的是：怎麼把決策從會議記錄裡抽出來、變成可追蹤的東西。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">可直接套用的會議記錄範本。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>會議名稱／時間／與會者</strong>——之後要找誰確認一目了然</li>
            <li><strong>議程</strong>——原本要討論的事項</li>
            <li><strong>決議事項</strong>——拍板了什麼。<em>這是會議記錄的核心</em></li>
            <li><strong>決策理由與前提</strong>——為什麼這樣決定（幾週後最想知道的就是這個）</li>
            <li><strong>待辦（負責人・期限）</strong>——接下來誰動</li>
            <li><strong>保留／下次再議</strong></li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.9, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            如果有錄音或線上會議，先做 <a href="/zh-tw/voice-to-text" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>語音轉文字</a>，再依這個架構整理——AI 會議記錄工具把「整理」這一步自動化了。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">會議記錄會躺在資料夾裡。<em>但決策不該被埋。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            用 AI 把會議記錄做出來，這件事已經被解決了。問題在後面——「決議事項」那幾行，沒和它影響的規格、它推翻的舊決策連在一起，就這樣放著。Draftlize 把那幾行變成可追蹤的卡片。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">決策變成「卡片」</h3>
            <p>把會議記錄的「決議事項」放進 Draftlize，就成為帶有決策、理由、負責人的卡片。不必抱著整份會議記錄，只有那幾個該活過這場會議的決策被用 ID 留下。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">新決策更新舊決策</h3>
            <p>今天的決定推翻了上個月的，連結著的舊決策和它上面的規格會自動 stale。下一份會議記錄不會再重議「已經拍板的事」。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">會議記錄 vs 決策紀錄</h3>
            <p>會議記錄是「會議的記錄」，決策紀錄是「決策的帳本」。記錄交給 AI，決策當成帳本保持最新——這個分工，解掉了決策悄悄過時的問題。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          AI 已經能把會議記錄做得很完整。它就是不會告訴你，那個決策現在還算不算數。
        </blockquote>
        <cite>記錄照做。把決策當帳本養著的，是 Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常見問題</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'AI 會議記錄免費嗎？', a: '很多 AI 會議記錄工具都有免費額度。Draftlize 不是會議記錄的產生工具——它把做好的會議記錄裡的「決議事項」，變成能保持最新的卡片。' },
            { q: '怎麼從錄音產生會議記錄？', a: '先語音轉文字，再依本頁的會議記錄範本整理。AI 會議記錄工具會把這個整理過程自動化；Draftlize 在那之後，把決策結構化並持續追蹤。' },
            { q: '決議事項要怎麼追蹤？', a: '把「決議事項」做成卡片，附上理由與負責人。之後若有別的決策推翻它，依賴它的地方會自動標記 stale，你就會知道哪個舊決策該回頭看。' },
            { q: '和有「待辦事項」的會議記錄工具差在哪？', a: '待辦是完成後就劃掉的任務。決策不一樣——有理由、有依賴、有壽命，上游一變就過時。Draftlize 會追蹤這種過時，這是待辦清單做不到的。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">把會議的決策，變成被追蹤的卡片<em>——免費送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            會議記錄工具照用。把決議事項交給 Draftlize，會議的決策就不再是記錄裡的一行字，而是一直保持最新的卡片。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
